import React, { useState, useMemo } from "react"
import PropTypes from "prop-types"
import { formatCurrency, formatNumber, formatPercent } from "../helpers"

const CargosTab = ({ data }) => {
  const c = data?.cargos
  const [selected, setSelected] = useState("__global__")

  const cargos = useMemo(() => {
    if (!c) return []
    if (selected === "__global__") return c.global?.cargos || []
    const suc = (c.sucursales || []).find(s => s.sucursal === selected)
    return suc?.cargos || []
  }, [c, selected])

  if (!c) {
    return (
      <div style={{ padding: 60, textAlign: "center", color: "#64748b" }}>
        <i className="mdi mdi-tag-text-outline" style={{ fontSize: 48, color: "#cbd5e1" }} />
        <div className="mt-2">Sin información de cargos.</div>
      </div>
    )
  }

  const totalCargos = cargos.reduce((sum, x) => sum + (Number(x.total) || 0), 0)
  const totalOps = cargos.reduce((sum, x) => sum + (Number(x.operaciones) || 0), 0)

  return (
    <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", padding: 20 }}>
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
        <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600 }}>
          <i className="mdi mdi-tag-text-outline me-2 text-primary" />
          Cargos ({cargos.length})
        </h6>
        <select
          className="form-select form-select-sm"
          style={{ minWidth: 220 }}
          value={selected}
          onChange={e => setSelected(e.target.value)}
        >
          <option value="__global__">Global (todas las sucursales)</option>
          {(c.sucursales || []).map(s => (
            <option key={s.sucursal} value={s.sucursal}>{s.sucursal}</option>
          ))}
        </select>
      </div>

      {/* Stats summary */}
      {cargos.length > 0 && (
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
          <div style={{ flex: "1 1 180px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "12px 14px" }}>
            <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600 }}>Total cargos</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#0f172a", marginTop: 2 }}>{formatCurrency(totalCargos)}</div>
          </div>
          <div style={{ flex: "1 1 180px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "12px 14px" }}>
            <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600 }}>Operaciones</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#0f172a", marginTop: 2 }}>{formatNumber(totalOps)}</div>
          </div>
          <div style={{ flex: "1 1 180px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "12px 14px" }}>
            <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600 }}>Tipos de cargo</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#0f172a", marginTop: 2 }}>{cargos.length}</div>
          </div>
        </div>
      )}

      {cargos.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", color: "#64748b" }}>
          Sin cargos para esta selección.
        </div>
      ) : (
        <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#f8fafc" }}>
                <th style={{ padding: "10px 12px", textAlign: "left", fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600, borderBottom: "1px solid #e2e8f0" }}>Razón</th>
                <th style={{ padding: "10px 12px", textAlign: "center", fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600, borderBottom: "1px solid #e2e8f0" }}>Código</th>
                <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600, borderBottom: "1px solid #e2e8f0" }}>Operaciones</th>
                <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600, borderBottom: "1px solid #e2e8f0" }}>Total</th>
                <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600, borderBottom: "1px solid #e2e8f0" }}>% prom.</th>
                <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600, borderBottom: "1px solid #e2e8f0" }}>Participación</th>
              </tr>
            </thead>
            <tbody>
              {cargos.map((cg, i) => {
                const pct = totalCargos > 0 ? ((Number(cg.total) || 0) / totalCargos) * 100 : 0
                return (
                  <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "10px 12px", color: "#0f172a", fontWeight: 500 }}>{cg.razon || "—"}</td>
                    <td style={{ padding: "10px 12px", textAlign: "center", color: "#64748b", fontFamily: "ui-monospace, SFMono-Regular, monospace", fontSize: 12 }}>{cg.codcargo || "—"}</td>
                    <td style={{ padding: "10px 12px", textAlign: "right", color: "#475569" }}>{formatNumber(cg.operaciones)}</td>
                    <td style={{ padding: "10px 12px", textAlign: "right", color: "#0f172a", fontWeight: 600 }}>{formatCurrency(cg.total)}</td>
                    <td style={{ padding: "10px 12px", textAlign: "right", color: "#475569" }}>{cg.porcentaje_promedio != null ? formatPercent(cg.porcentaje_promedio) : "—"}</td>
                    <td style={{ padding: "10px 12px", textAlign: "right" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8 }}>
                        <div style={{ width: 60, height: 6, background: "#e2e8f0", borderRadius: 3, overflow: "hidden" }}>
                          <div style={{ width: `${Math.min(100, pct)}%`, height: "100%", background: "#3b82f6" }} />
                        </div>
                        <span style={{ color: "#475569", fontSize: 12, minWidth: 50, textAlign: "right" }}>
                          {formatPercent(pct)}
                        </span>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

CargosTab.propTypes = { data: PropTypes.object }

export default CargosTab
